import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const { user } = useAuth();

  const fetchNotifications = useCallback(async (includeRead = false) => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/notifications/?include_read=${includeRead}`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user]);

  const fetchPreferences = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, [user]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      await fetchNotifications(true);
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      await fetchNotifications(true);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const archiveNotification = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/archive`);
      await fetchNotifications(true);
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      await fetchNotifications(true);
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAllRead = async () => {
    try {
      await api.delete('/notifications/clear-all');
      await fetchNotifications(true);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const updatePreferences = async (newPreferences) => {
    try {
      await api.put('/notifications/preferences', newPreferences);
      await fetchPreferences();
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user) return;

    // Set up polling for notifications (since WebSocket is complex)
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // Check every 30 seconds

    // Initial fetch
    fetchNotifications();
    fetchPreferences();

    return () => clearInterval(interval);
  }, [user, fetchNotifications, fetchUnreadCount, fetchPreferences]);

  // Show browser notification when unread count increases
  useEffect(() => {
    if (unreadCount > 0 && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const lastCount = parseInt(localStorage.getItem('lastUnreadCount') || '0');
      if (unreadCount > lastCount) {
        try {
          new Notification('UniResolve', {
            body: `You have ${unreadCount} new notification${unreadCount > 1 ? 's' : ''}`,
            icon: '/favicon.ico'
          });
        } catch (e) {
          // Notification API not available in this context
        }
      }
      localStorage.setItem('lastUnreadCount', unreadCount);
    }
  }, [unreadCount]);

  // Request notification permission
  useEffect(() => {
    if (typeof Notification !== 'undefined' &&
        Notification.permission !== 'granted' &&
        Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      preferences,
      fetchNotifications,
      fetchUnreadCount,
      markAsRead,
      markAllAsRead,
      archiveNotification,
      deleteNotification,
      clearAllRead,
      updatePreferences
    }}>
      {children}
    </NotificationContext.Provider>
  );
};